import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import type { NsfwPresetLevel, AccuracyLevel } from "@/config/nsfwRulePresets";

interface DeviceBrowserSettingsContentProps {
  deviceId: string;
  browserMode: "filtered" | "whitelist" | "none";
  onBrowserModeChange: (mode: "filtered" | "whitelist" | "none") => void;
  nsfwLevel: NsfwPresetLevel;
  onNsfwLevelChange: (level: NsfwPresetLevel) => void;
  human: boolean;
  onHumanChange: (value: boolean) => void;
  accuracy: AccuracyLevel;
  onAccuracyChange: (value: AccuracyLevel) => void;
}

export const DeviceBrowserSettingsContent = ({
  deviceId,
  browserMode,
  onBrowserModeChange,
  nsfwLevel,
  onNsfwLevelChange,
  human,
  onHumanChange,
  accuracy,
  onAccuracyChange,
}: DeviceBrowserSettingsContentProps) => {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-lg font-semibold">Browser Settings</h1>
        <p className="text-sm text-muted-foreground">Configure browser access and content filtering for this device</p>
      </div>

      {/* Browser Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Browser Configuration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <RadioGroup
            value={browserMode}
            onValueChange={(val: "filtered" | "whitelist" | "none") => onBrowserModeChange(val)}
          >
            <div className="flex items-start gap-3 p-3 rounded-lg border">
              <RadioGroupItem value="filtered" id="browser-filtered" />
              <div>
                <Label htmlFor="browser-filtered" className="font-medium">Filtered Browser</Label>
                <p className="text-sm text-muted-foreground">Allow browser with category filters and optional blacklists.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 rounded-lg border">
              <RadioGroupItem value="whitelist" id="browser-whitelist" />
              <div>
                <Label htmlFor="browser-whitelist" className="font-medium">Whitelisted Browser</Label>
                <p className="text-sm text-muted-foreground">Allow only explicitly whitelisted sites. Categories and blacklist are hidden.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 rounded-lg border">
              <RadioGroupItem value="none" id="browser-none" />
              <div>
                <Label htmlFor="browser-none" className="font-medium">No Browser</Label>
                <p className="text-sm text-muted-foreground">Completely block all browser access. Only Settings remain visible.</p>
              </div>
            </div>
          </RadioGroup>
        </CardContent>
      </Card>

      {/* Content Filtering - only show when browser is enabled */}
      {browserMode !== "none" && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Content Filtering</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Content Filtering Level */}
            <div className="space-y-2">
              <div className="font-medium">Content Filtering Level</div>
              <p className="text-sm text-muted-foreground">Choose the content filtering level for images.</p>
              <RadioGroup
                value={nsfwLevel}
                onValueChange={(val: NsfwPresetLevel) => onNsfwLevelChange(val)}
              >
                <div className="flex items-start gap-3 p-3 rounded-lg border">
                  <RadioGroupItem value="light" id="nsfw-light" />
                  <div>
                    <Label htmlFor="nsfw-light" className="font-medium">Light</Label>
                    <p className="text-sm text-muted-foreground">Blocks explicit adult content only</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 rounded-lg border">
                  <RadioGroupItem value="medium" id="nsfw-medium" />
                  <div>
                    <Label htmlFor="nsfw-medium" className="font-medium">Medium</Label>
                    <p className="text-sm text-muted-foreground">Blocks explicit + suggestive imagery</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 rounded-lg border">
                  <RadioGroupItem value="strict" id="nsfw-strict" />
                  <div>
                    <Label htmlFor="nsfw-strict" className="font-medium">Strict</Label>
                    <p className="text-sm text-muted-foreground">Blocks immodest or inappropriate dress</p>
                  </div>
                </div>
              </RadioGroup>
            </div>

            {/* People Blocking Toggle */}
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <div className="font-medium">People Blocking</div>
                <p className="text-sm text-muted-foreground">Blur images containing people</p>
              </div>
              <Switch 
                checked={human} 
                onCheckedChange={onHumanChange}
                variant="success-danger"
              />
            </div>

            {/* Accuracy Setting */}
            <div className="space-y-2">
              <div className="font-medium">Filtering Speed</div>
              <p className="text-sm text-muted-foreground">Balance between filtering accuracy and performance.</p>
              <RadioGroup
                value={String(accuracy)}
                onValueChange={(val) => onAccuracyChange(Number(val) as AccuracyLevel)}
              >
                <div className="flex items-start gap-3 p-3 rounded-lg border">
                  <RadioGroupItem value="0" id="accuracy-accurate" />
                  <div>
                    <Label htmlFor="accuracy-accurate" className="font-medium">Accurate</Label>
                    <p className="text-sm text-muted-foreground">Most precise filtering, slower performance</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 rounded-lg border">
                  <RadioGroupItem value="1" id="accuracy-balanced" />
                  <div>
                    <Label htmlFor="accuracy-balanced" className="font-medium">Balanced</Label>
                    <p className="text-sm text-muted-foreground">Good balance of speed and accuracy</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 rounded-lg border">
                  <RadioGroupItem value="2" id="accuracy-fast" />
                  <div>
                    <Label htmlFor="accuracy-fast" className="font-medium">Fast</Label>
                    <p className="text-sm text-muted-foreground">Fastest performance, slightly less precise (default)</p>
                  </div>
                </div>
              </RadioGroup>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
